import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function FAQ({ items = DEFAULT_FAQ, title, description, hideHeader = false }) {
  return (
    <section data-testid="faq-section" className={`relative ${hideHeader ? "pt-0 pb-16 sm:pb-20" : "py-16 sm:py-20"}`}>
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        {!hideHeader && (
        <div className="mb-10 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-muted-foreground mb-4">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-gradient" />
            FAQ
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05]">
            {title || "Answers, honestly."}
          </h2>
          {description && (
            <p className="mt-4 text-sm text-muted-foreground max-w-xl mx-auto">{description}</p>
          )}
        </div>
        )}

        <Accordion type="single" collapsible className="w-full">
          {items.map((it, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              data-testid={`faq-item-${i}`}
              className="border-b hairline py-2"
            >
              <AccordionTrigger className="text-left font-heading text-lg lg:text-xl font-medium hover:no-underline">
                <span className="flex items-start gap-4">
                  <span className="font-mono text-xs text-muted-foreground mt-1.5">
                    {i + 1}
                  </span>
                  <span>{it.q}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pl-11 pb-6 text-sm sm:text-base text-muted-foreground leading-relaxed">
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export const DEFAULT_FAQ = [
  { q: "What is Rxn3D?", a: "Rxn3D is cloud-based dental laboratory management platform. It connects dental offices, labs and drivers around a single case, from digital prescription to final delivery." },
  { q: "Who is Rxn3D for?", a: "Modern dental labs, dental offices, orthodontic and implant centers, multi-location dental groups and their drivers/couriers." },
  { q: "Does Rxn3D replace paper prescriptions?", a: "Yes. Doctors submit structured digital prescriptions with tooth chart, materials, shade, notes and attachments, all in seconds." },
  { q: "Can I track every case in real time?", a: "Absolutely. Every case has a live timeline of milestones and can be filtered by status, technician, station, doctor, and more." },
  { q: "How does billing work?", a: "Rxn3D can auto-generate invoices from the prescription and case activity, reconcile with delivery, and export accounting-ready reports." },
  { q: "Do you offer driver routing?", a: "Yes. Drivers see optimized pickup and delivery routes, capture signatures and photos, and every delivery is timestamped." },
  { q: "Is there a free trial?", a: "You can request a demo and we’ll walk you through a personalized tour. Trial availability depends on your plan." },
  { q: "Do you support multiple locations?", a: "Yes. Enterprise supports unlimited locations with consolidated dashboards, and per-location permissions." },
  { q: "Can I integrate my scanner or CAD software?", a: "Rxn3D accepts STL, DCM, PLY and common image formats as attachments. Native integrations with popular scanners and CAD software are on our roadmap." },
  { q: "How secure is my data?", a: "Data is encrypted in transit (TLS 1.2+) and at rest (AES-256). Access is controlled through granular roles and every action is auditable." },
  { q: "Is Rxn3D HIPAA compliant?", a: "Rxn3D is designed with HIPAA principles in mind. We provide the controls, logs and BAA-friendly workflows that support HIPAA-conscious operations." },
  { q: "What happens to my data if I cancel?", a: "You can export your data at any time. On cancellation we retain data per your billing plan and then delete or anonymize according to policy." },
  { q: "Can I cancel anytime?", a: "Yes. Monthly plans can be cancelled at the end of the current billing cycle." },
  { q: "Do you offer onboarding?", a: "Every plan includes structured onboarding. Enterprise includes a dedicated Customer Success Manager and migration support." },
  { q: "Which browsers are supported?", a: "The latest two versions of Chrome, Edge, Firefox and Safari on desktop, iPadOS and iOS/Android on mobile." },
  { q: "Do you support SSO?", a: "SSO (SAML/OIDC) is available on Enterprise plans." },
  { q: "How do notifications work?", a: "In-app, email and SMS notifications can be configured per role and per event: new case, status change, delivery, invoice etc." },
  { q: "Can doctors sign digitally?", a: "Yes. Doctors approve prescriptions with a digital signature captured on device." },
  { q: "Does Rxn3D support multi-currency?", a: "Multi-currency invoicing is available on Professional and Enterprise plans." },
  { q: "How do I contact support?", a: "Use the contact form on our website for help, pricing, and demos. Enterprise plans include priority channels." },
];
